import { Suspense, lazy, useEffect, useState } from "react";
import { ConfirmProvider } from "../core/confirm/ConfirmProvider";
import RemoteSessionBanner from "../core/auth/RemoteSessionBanner";
import PushNotificationBanner from "../components/notifications/PushNotificationBanner";
import PwaInstallBanner from "../components/PwaInstallBanner";
import LocationPrompt from "../features/location/LocationPrompt";
const DesktopLayout = lazy(() => import("../layouts/desktop/DesktopLayout"));
const MobileLayout = lazy(() => import("../layouts/mobile/MobileLayout"));
// À cette largeur, le layout desktop n'a plus assez d'espace pour conserver
// ses rails et panneaux. Ce seuil est aligné avec la détection compacte de
// l'authentification afin qu'une fenêtre desktop réduite reproduise bien la
// vue mobile utilisée sur téléphone et tablette en portrait.
const MOBILE_LAYOUT_QUERY = "(max-width: 820px)";

function resolveIsMobileLayout() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia(MOBILE_LAYOUT_QUERY).matches
  );
}

function RootLayoutFallback({ isMobile }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? "24px" : "32px",
        background:
          "radial-gradient(circle at 50% 0%, rgba(96,165,250,.16), transparent 34%), linear-gradient(180deg, rgba(6,11,28,.98), rgba(4,8,22,1))",
        color: "rgba(241,245,249,.88)",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        style={{
          width: "min(100%, 360px)",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: isMobile ? "18px 18px 20px" : "22px 22px 24px",
          borderRadius: "24px",
          border: "1px solid rgba(148,163,184,.14)",
          background:
            "linear-gradient(180deg, rgba(15,23,42,.78), rgba(8,12,22,.92))",
          boxShadow:
            "0 24px 46px rgba(2,6,23,.34), inset 0 1px 0 rgba(255,255,255,.06)",
          backdropFilter: "blur(16px)",
        }}
      >
        <strong style={{ fontSize: "13px", letterSpacing: ".08em", textTransform: "uppercase" }}>
          Chargement de l interface
        </strong>
        <span style={{ fontSize: "14px", lineHeight: 1.6, color: "rgba(226,232,240,.72)" }}>
          Preparation de la version {isMobile ? "mobile" : "desktop"}.
        </span>
      </div>
    </div>
  );
}

export default function RootLayout() {
  const [isMobile, setIsMobile] = useState(resolveIsMobileLayout);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MOBILE_LAYOUT_QUERY);
    const syncLayoutMode = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === "function") {
      mediaQueryList.addEventListener("change", syncLayoutMode);
      return () => {
        mediaQueryList.removeEventListener("change", syncLayoutMode);
      };
    }

    mediaQueryList.addListener(syncLayoutMode);
    return () => {
      mediaQueryList.removeListener(syncLayoutMode);
    };
  }, []);

  return (
    <ConfirmProvider>
      <Suspense fallback={<RootLayoutFallback isMobile={isMobile} />}>
        {isMobile ? <MobileLayout /> : <DesktopLayout />}
      </Suspense>
      <RemoteSessionBanner />
      <PwaInstallBanner isMobile={isMobile} />
      <PushNotificationBanner />
      <LocationPrompt />
    </ConfirmProvider>
  );
}
